import createTempHtmlEditor from './createTempHtmlEditor';
import initializeUndoSnapshot from '../actions/initializeUndoSnapshot';
import setPlainTextEditorSelection from '../actions/setPlainTextEditorSelection';
import updateContent from '../actions/updateContent';
import { addSnapshotAction } from '../internalPlugin/Undo';
import { CompositeEditorViewState } from '../store/schema/CompositeEditorViewState';
import EditorViewState from '../store/schema/EditorViewState';
import { Editor } from 'roosterjs-editor-core';
import * as domApi from 'roosterjs-editor-dom';

interface ContentOperator {
    htmlEditor: Editor;
    plainTextEditor: HTMLTextAreaElement;
}

interface HtmlEditorCallbackResponse {
    selectionRange: Range;
    changeSource?: string;
}

interface PlainTextEditorCallbackResponse {
    value: string;
    selectionStart: number;
    selectionEnd: number;
    focus?: boolean;
}

export type OperateContentType = typeof operateContent;

let contentOperatorMap: { [editorId: number]: ContentOperator } = {};

export function addContentOperator(
    viewState: EditorViewState,
    htmlEditor: Editor,
    plainTextEditor: HTMLTextAreaElement
) {
    const operator: ContentOperator = {
        htmlEditor: htmlEditor,
        plainTextEditor: plainTextEditor,
    };
    contentOperatorMap[viewState.editorId] = operator;
}

export function removeContentOperator(viewState: EditorViewState) {
    delete contentOperatorMap[viewState.editorId];
}

// NOTE: Do not export this!! All external functions should use "operateContent"
function findContentOperator(viewState: EditorViewState): ContentOperator {
    return contentOperatorMap[viewState.editorId];
}

export default function operateContent(
    viewState: EditorViewState,
    htmlCallback: (
        htmlEditor: Editor,
        originalRange: Range,
        domUtils: typeof domApi
    ) => HtmlEditorCallbackResponse
);

export default function operateContent(
    viewState: CompositeEditorViewState,
    htmlCallback: (
        htmlEditor: Editor,
        originalRange: Range,
        domUtils: typeof domApi
    ) => HtmlEditorCallbackResponse,
    plainTextCallback: (
        value: string,
        selectionStart: number,
        selectionEnd: number
    ) => PlainTextEditorCallbackResponse
);

export default function operateContent(
    viewState: EditorViewState,
    htmlCallback: (
        htmlEditor: Editor,
        originalRange: Range,
        domUtils: typeof domApi
    ) => HtmlEditorCallbackResponse,
    plainTextCallback?: (
        value: string,
        selectionStart: number,
        selectionEnd: number
    ) => PlainTextEditorCallbackResponse
) {
    let content: string;
    const operator: ContentOperator = findContentOperator(viewState);
    const bodyType = (viewState as CompositeEditorViewState).bodyType;

    // bodyType doesn't exist in EditorViewState, so we treat undefined as HTML
    const isHTML = bodyType === undefined || bodyType === 'HTML';
    const isText = bodyType === 'Text';

    if (isHTML && htmlCallback) {
        const htmlEditor: Editor = operator?.htmlEditor;
        if (htmlEditor) {
            // CASE 1: HTML, editor already initialized
            // Directly operate on editor to add undo snapshots (before and after callback),
            // update content, set selection range, and trigger content changed event

            htmlEditor.addUndoSnapshot();

            const response = htmlCallback(htmlEditor, htmlEditor.getSelectionRange(), domApi);
            if (response) {
                const { selectionRange, changeSource } = response;
                if (selectionRange) {
                    htmlEditor.select(selectionRange);
                }
                if (changeSource) {
                    htmlEditor.triggerContentChangedEvent(changeSource);
                }
            }
            content = htmlEditor.getContent();

            htmlEditor.addUndoSnapshot();
        } else {
            // CASE 2: HTML, editor not yet initialized
            // Create a temporary editor to operate on and save its response to the view state
            // so the real editor will have the updated content, selection range, and undo
            // snapshots (before and after the callback)

            if (!viewState.undoSnapshot) {
                initializeUndoSnapshot(viewState);
            }
            addSnapshotAction(viewState.undoSnapshot, viewState.content);

            const tempDiv = document.createElement('div');
            const tempEditor = createTempHtmlEditor(tempDiv, viewState.content);

            // TODO: get correct range for disposed editor case
            const response = htmlCallback(tempEditor, tempEditor.getSelectionRange(), domApi);
            // if selectionRange is null, no selection path will be added
            content = domApi.getHtmlWithSelectionPath(tempDiv, response?.selectionRange);

            tempEditor.dispose();
            addSnapshotAction(viewState.undoSnapshot, content);
        }
    } else if (isText && plainTextCallback) {
        const plainTextEditor = operator?.plainTextEditor;
        if (plainTextEditor) {
            // CASE 3: Plain Text, editor already initialized
            // Directly set the value and selection of the response to the editor

            const response = plainTextCallback(
                plainTextEditor.value,
                plainTextEditor.selectionStart,
                plainTextEditor.selectionEnd
            );
            if (response) {
                const { value, selectionStart, selectionEnd, focus } = response;
                content = plainTextEditor.value = value;
                if (focus) {
                    plainTextEditor.focus();
                    const newSelectionStart = selectionStart || 0;
                    const newSelectionEnd = selectionEnd || 0;
                    plainTextEditor.selectionStart = newSelectionStart;
                    plainTextEditor.selectionEnd = newSelectionEnd;
                    setPlainTextEditorSelection(viewState, newSelectionStart, newSelectionEnd);
                }
            }
        } else {
            // CASE 4: Plain Text, editor not yet initialized
            // Save the response to the view state so that when the editor is created
            // and mounted it will have the correct content and selection

            const response = plainTextCallback(
                viewState.content /* value */,
                viewState.selectionStart || 0 /* selectionStart */,
                viewState.selectionEnd || 0 /* selectionEnd */
            );
            if (response) {
                const { value, selectionStart, selectionEnd } = response;
                content = value;
                setPlainTextEditorSelection(viewState, selectionStart || 0, selectionEnd || 0);
            }
        }
    }

    // In all cases, save operated content to the view state
    if (content != null) {
        updateContent(viewState, content);
    }
}
